import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  ChangeDetectorRef,
  LOCALE_ID
} from '@angular/core';
import { formatNumber } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BreakInOutDecision, BreakInOutStatus, Client, InitiativeStage, IOnePPInitiativeActualCostTotalsFlagsDTO, OnePInitiativeWithBreakInOutDTO, OnePMasterLinkedProjectsDTO, OnePPInitiativeActualCostTotalsFlagsDTO, OnePProjectCostTotalsDTO, UpdateForecastUpdatedFieldCommand } from '../../../api/api-clientInformation';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { CostTotalsEditModel } from '../../report-form/models/cost-totals-edit-model';
import { finalize, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { Roles } from '../../report-form/constants';
import { CostTotalsService } from '../../../services/cost-totals.service';
import { LoaderService } from '../loader/loader.service';

@Component({
  selector: 'app-project-totals-dialog',
  standalone: false,
  templateUrl: './project-totals-dialog.component.html',
  styleUrl: './project-totals-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectTotalsDialogComponent implements OnInit, AfterViewInit {
  apiClient = inject(Client);
  protected readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  // The root instance is the one the interceptor and app.component use; lazy modules provide their own copies.
  private readonly loader = inject(ApplicationRef).injector.get(LoaderService);
  private readonly locale = inject(LOCALE_ID);
  private destroy$ = new Subject<void>();
  private changedFields = new Set<string>();
  private changeBeforeAmounts = new Map<string, number>();
  isForecastOpened = false;
  loading = false;
  isForForecasts = false;
  // title = 'Project Totals FY';
  showInK = true; // default ON
  description: string | undefined;
  hasPendingBreakInOut = false;
  isFullAccess = false;
  isEditingActuals = false;
  stage: InitiativeStage | undefined;
  private _fb: FormBuilder = new FormBuilder();
  public projectTotalsForm!: FormGroup;
  initiativeActualCostTotalsEditedFlags: IOnePPInitiativeActualCostTotalsFlagsDTO = {};
  forPastMonthTotal: any;
  private initialFormValues: any = {};
  protected isSaveAllowed = signal(true);
  protected lastMonthFyOutlook: number | null = null;
  protected lastMonthCarriedOverFyOutlook: number | null = null;
  protected lastMonthGrandTotal: number | null = null;
  protected carriedOverFyOutlook: number | null = null;
  indicatedAmount: number | null = null;


  protected noChanges = signal(false);
  canAddBreakInOut = false;
  isReadOnlyUser = this.authService.isInRole(Roles.ReadOnly);

  carriedOverCostTotals: OnePProjectCostTotalsDTO | null = null;
  carriedOver: OnePMasterLinkedProjectsDTO | null = null;
  isCarriedOverSectionExpanded = false;
  readonly carriedOverMonths = [
    { key: 'jan', label: 'COMMON.MONTHS_SHORT.JAN' },
    { key: 'feb', label: 'COMMON.MONTHS_SHORT.FEB' },
    { key: 'mar', label: 'COMMON.MONTHS_SHORT.MAR' },
    { key: 'apr', label: 'COMMON.MONTHS_SHORT.APR' },
    { key: 'may', label: 'COMMON.MONTHS_SHORT.MAY' },
    { key: 'jun', label: 'COMMON.MONTHS_SHORT.JUN' },
    { key: 'jul', label: 'COMMON.MONTHS_SHORT.JUL' },
    { key: 'aug', label: 'COMMON.MONTHS_SHORT.AUG' },
    { key: 'sep', label: 'COMMON.MONTHS_SHORT.SEP' },
    { key: 'oct', label: 'COMMON.MONTHS_SHORT.OCT' },
    { key: 'nov', label: 'COMMON.MONTHS_SHORT.NOV' },
    { key: 'dec', label: 'COMMON.MONTHS_SHORT.DEC' },
  ];

  protected compareInitiativeCostTotals = (): boolean => {
    var changed = false;
    if (this.projectTotalsForm.valid) {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const formValues = this.projectTotalsForm.getRawValue();

      const monthMapping: { [key: string]: number } = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };

      Object.keys(this.data.initiative.initiativeCostTotals).forEach((key) => {
        const monthKey = key.toLowerCase().substring(0, 3);
        const selectedYear = this.data.year || currentYear;
        const isFutureMonth = selectedYear > currentYear || (selectedYear === currentYear && monthMapping[monthKey] >= currentMonth);
        const initialValue = this.data.initiative.initiativeCostTotals[key] ?? 0;
        const currentValue = formValues[key] ?? 0;

        if (isFutureMonth && initialValue !== currentValue) {
          changed = true;
        }
      });
    }
    return changed;
  };

  private materialJanFormControl = this._fb.control(0);
  private materialFebFormControl = this._fb.control(0);
  private materialMarFormControl = this._fb.control(0);
  private materialAprFormControl = this._fb.control(0);
  private materialMayFormControl = this._fb.control(0);
  private materialJunFormControl = this._fb.control(0);
  private materialJulFormControl = this._fb.control(0);
  private materialAugFormControl = this._fb.control(0);
  private materialSepFormControl = this._fb.control(0);
  private materialOctFormControl = this._fb.control(0);
  private materialNovFormControl = this._fb.control(0);
  private materialDecFormControl = this._fb.control(0);
  private serviceJanFormControl = this._fb.control(0);
  private serviceFebFormControl = this._fb.control(0);
  private serviceMarFormControl = this._fb.control(0);
  private serviceAprFormControl = this._fb.control(0);
  private serviceMayFormControl = this._fb.control(0);
  private serviceJunFormControl = this._fb.control(0);
  private serviceJulFormControl = this._fb.control(0);
  private serviceAugFormControl = this._fb.control(0);
  private serviceSepFormControl = this._fb.control(0);
  private serviceOctFormControl = this._fb.control(0);
  private serviceNovFormControl = this._fb.control(0);
  private serviceDecFormControl = this._fb.control(0);
  private otherJanFormControl = this._fb.control(0);
  private otherFebFormControl = this._fb.control(0);
  private otherMarFormControl = this._fb.control(0);
  private otherAprFormControl = this._fb.control(0);
  private otherMayFormControl = this._fb.control(0);
  private otherJunFormControl = this._fb.control(0);
  private otherJulFormControl = this._fb.control(0);
  private otherAugFormControl = this._fb.control(0);
  private otherSepFormControl = this._fb.control(0);
  private otherOctFormControl = this._fb.control(0);
  private otherNovFormControl = this._fb.control(0);
  private otherDecFormControl = this._fb.control(0);
  private totalJanFormControl = this._fb.control(0);
  private totalFebFormControl = this._fb.control(0);
  private totalMarFormControl = this._fb.control(0);
  private totalAprFormControl = this._fb.control(0);
  private totalMayFormControl = this._fb.control(0);
  private totalJunFormControl = this._fb.control(0);
  private totalJulFormControl = this._fb.control(0);
  private totalAugFormControl = this._fb.control(0);
  private totalSepFormControl = this._fb.control(0);
  private totalOctFormControl = this._fb.control(0);
  private totalNovFormControl = this._fb.control(0);
  private totalDecFormControl = this._fb.control(0);
  private totalMaterialFormControl = this._fb.control(0);
  private totalServiceFormControl = this._fb.control(0);
  private totalOtherFormControl = this._fb.control(0);
  private totalGeneralFormControl = this._fb.control(0);
  private totalGeneral = 0;

  private initiative: any;
  breakInCount = signal(0);
  allBreakInsDeclined = signal(true);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ProjectTotalsDialogComponent>,
    private dialog: MatDialog,
    private costTotalsService: CostTotalsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef
  ) {
    this._fb = fb;
  }

  ngAfterViewInit() {
    if (this.data.isForForecasts) {
      if (this.data.mergeData) {
        this.projectTotalsForm.patchValue(this.data.mergeData);
        this.calculateTotals();
        this.lastMonthFyOutlook = this.data.lastMonthFyOutlook;
        this.lastMonthCarriedOverFyOutlook = this.data.lastMonthCarriedOverFyOutlook ?? 0;
        this.carriedOverFyOutlook = this.data.carriedOverFyOutlook ?? 0;
        // Calculate after lastMonthFyOutlook is set
        this.calculateCanEditBreakInOut();
        // console.log(this.canAddBreakInOut);

      }
    } else {
      if (this.data.onePProjectTotals) {
        this.projectTotalsForm.patchValue(this.data.onePProjectTotals);
        this.calculateTotals();
        this.lastMonthFyOutlook = this.data.lastMonthFyOutlook;
        this.lastMonthCarriedOverFyOutlook = this.data.lastMonthCarriedOverFyOutlook ?? 0;
      }
    }

    this.lastMonthGrandTotal = (this.lastMonthFyOutlook ?? 0) + (this.lastMonthCarriedOverFyOutlook ?? 0);

    this.initialFormValues = { ...this.projectTotalsForm.value };
    // if (this.data.initiative) {
    //   this.initiative = this.data.initiative;
    // }
    if (this.data.breakInCount || this.data.allBreakInsDeclined) {
      this.breakInCount.set(this.data.breakInCount);
      this.allBreakInsDeclined.set(this.data.allBreakInsDeclined);
    }


    if (this.data.onePastProjectTotals) {
    }

    if (this.data.description) {
      this.description = this.data.description;
      this.cdr.detectChanges();
    }
    if (this.data.stage) {
      this.stage = this.data.stage;
    }
    if (this.data.initiativeActualCostTotalsEditedFlags) {
      this.initiativeActualCostTotalsEditedFlags =
        this.data.initiativeActualCostTotalsEditedFlags;
    }
    this.hasPendingBreakInOut = this.data.hasPendingBreakInOut;


    if (this.stage === InitiativeStage.Lookback || this.stage === InitiativeStage.Complete) {
      this.projectTotalsForm.disable();
      this.isSaveAllowed.set(false);
    }
    if (this.data.isForForecasts) {
      if (this.compareInitiativeCostTotals()) {
        this.noChanges.set(false);
      } else {
        this.noChanges.set(true);
      }
    }
    this.carriedOverCostTotals = this.data.carriedOverCostTotals ?? null;
    this.carriedOver = this.data.carriedOver ?? null;
    if (this.hasPendingBreakInOut && this.data.pendingBreakInOutId != null) {
      this.loadPendingBreakInChanges(this.data.pendingBreakInOutId);
    }
  }

  // Marks fields changed by the latest pending break-in so the template can show "current / old".
  private loadPendingBreakInChanges(breakInOutId: number): void {
    const initiativeId = this.data.initiative?.pk;
    if (initiativeId == null) {
      return;
    }

    this.apiClient.getForecastCostTotalsChangeHistory(initiativeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (changeSets) => {
          this.changedFields.clear();
          this.changeBeforeAmounts.clear();
          // A field may appear in several change sets (even reverted back): keep the oldest "before".
          (changeSets ?? [])
            .filter(changeSet => changeSet.breakInOutFk === breakInOutId)
            .sort((a, b) => (a.changeSetId ?? 0) - (b.changeSetId ?? 0))
            .forEach(changeSet => {
              changeSet.lineDTOs?.forEach(line => {
                if (!line.columnName || (line.year != null && line.year !== this.data.year)) {
                  return;
                }
                const fieldName = line.columnName.charAt(0).toLowerCase() + line.columnName.slice(1);
                this.changedFields.add(fieldName);
                if (!this.changeBeforeAmounts.has(fieldName)) {
                  this.changeBeforeAmounts.set(fieldName, line.beforeAmount ?? 0);
                }
              });
            });
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching pending break-in changes:', error);
        }
      });
  }

  isFieldChanged(fieldName: string): boolean {
    return this.changedFields.has(fieldName);
  }

  getOldValue(fieldName: string): string {
    const before = this.changeBeforeAmounts.get(fieldName) ?? 0;
    return formatNumber(this.showInK ? before / 1000 : before, this.locale, '1.0-0');
  }

  getCarriedOverValue(month: string, type: 'Material' | 'Service' | 'Other'): number {
    if (!this.carriedOverCostTotals) return 0;
    return (this.carriedOverCostTotals as any)[`${month}${type}`] ?? 0;
  }

  getCarriedOverRowTotal(month: string): number {
    return this.getCarriedOverValue(month, 'Material')
      + this.getCarriedOverValue(month, 'Service')
      + this.getCarriedOverValue(month, 'Other');
  }

  getCarriedOverColTotal(type: 'Material' | 'Service' | 'Other'): number {
    return this.carriedOverMonths.reduce(
      (sum, m) => sum + this.getCarriedOverValue(m.key, type), 0
    );
  }

  toggleCarriedOver(): void {
    this.isCarriedOverSectionExpanded = !this.isCarriedOverSectionExpanded;
    this.dialogRef.updateSize(this.isCarriedOverSectionExpanded ? '1400px' : '800px');
    this.cdr.markForCheck();
  }

  isPowerUserStaged(): boolean {
    return this.authService.isInRole(Roles.PowerUser) && (this.stage == InitiativeStage.Execution || this.stage == InitiativeStage.PreExecution);
  }

  private isPreExecutionPlanningStage(): boolean {
    const stage = this.data.stage ?? this.data.initiative?.stage;
    return stage === InitiativeStage.Funnel
      || stage === InitiativeStage.Review
      || stage === InitiativeStage.Analysis
      || stage === InitiativeStage.Backlog;
  }

  private disablePastMonths(monthIndex: number) {
    if (monthIndex >= 1) {
      this.materialJanFormControl.disable();
      this.serviceJanFormControl.disable();
      this.otherJanFormControl.disable();
    }
    if (monthIndex >= 2) {
      this.materialFebFormControl.disable();
      this.serviceFebFormControl.disable();
      this.otherFebFormControl.disable();
    }
    if (monthIndex >= 3) {
      this.materialMarFormControl.disable();
      this.serviceMarFormControl.disable();
      this.otherMarFormControl.disable();
    }
    if (monthIndex >= 4) {
      this.materialAprFormControl.disable();
      this.serviceAprFormControl.disable();
      this.otherAprFormControl.disable();
    }
    if (monthIndex >= 5) {
      this.materialMayFormControl.disable();
      this.serviceMayFormControl.disable();
      this.otherMayFormControl.disable();
    }
    if (monthIndex >= 6) {
      this.materialJunFormControl.disable();
      this.serviceJunFormControl.disable();
      this.otherJunFormControl.disable();
    }
    if (monthIndex >= 7) {
      this.materialJulFormControl.disable();
      this.serviceJulFormControl.disable();
      this.otherJulFormControl.disable();
    }
    if (monthIndex >= 8) {
      this.materialAugFormControl.disable();
      this.serviceAugFormControl.disable();
      this.otherAugFormControl.disable();
    }
    if (monthIndex >= 9) {
      this.materialSepFormControl.disable();
      this.serviceSepFormControl.disable();
      this.otherSepFormControl.disable();
    }
    if (monthIndex >= 10) {
      this.materialOctFormControl.disable();
      this.serviceOctFormControl.disable();
      this.otherOctFormControl.disable();
    }
    if (monthIndex >= 11) {
      this.materialNovFormControl.disable();
      this.serviceNovFormControl.disable();
      this.otherNovFormControl.disable();
    }
    if (monthIndex >= 12) {
      this.materialDecFormControl.disable();
      this.serviceDecFormControl.disable();
      this.otherDecFormControl.disable();
    }
  }
  private disablefutureMonths(monthIndex: number) {
    if (monthIndex < 1) {
      this.materialJanFormControl.disable();
      this.serviceJanFormControl.disable();
      this.otherJanFormControl.disable();
    }
    if (monthIndex < 2) {
      this.materialFebFormControl.disable();
      this.serviceFebFormControl.disable();
      this.otherFebFormControl.disable();
    }
    if (monthIndex < 3) {
      this.materialMarFormControl.disable();
      this.serviceMarFormControl.disable();
      this.otherMarFormControl.disable();
    }
    if (monthIndex < 4) {
      this.materialAprFormControl.disable();
      this.serviceAprFormControl.disable();
      this.otherAprFormControl.disable();
    }
    if (monthIndex < 5) {
      this.materialMayFormControl.disable();
      this.serviceMayFormControl.disable();
      this.otherMayFormControl.disable();
    }
    if (monthIndex < 6) {
      this.materialJunFormControl.disable();
      this.serviceJunFormControl.disable();
      this.otherJunFormControl.disable();
    }
    if (monthIndex < 7) {
      this.materialJulFormControl.disable();
      this.serviceJulFormControl.disable();
      this.otherJulFormControl.disable();
    }
    if (monthIndex < 8) {
      this.materialAugFormControl.disable();
      this.serviceAugFormControl.disable();
      this.otherAugFormControl.disable();
    }
    if (monthIndex < 9) {
      this.materialSepFormControl.disable();
      this.serviceSepFormControl.disable();
      this.otherSepFormControl.disable();
    }
    if (monthIndex < 10) {
      this.materialOctFormControl.disable();
      this.serviceOctFormControl.disable();
      this.otherOctFormControl.disable();
    }
    if (monthIndex < 11) {
      this.materialNovFormControl.disable();
      this.serviceNovFormControl.disable();
      this.otherNovFormControl.disable();
    }
    if (monthIndex < 12) {
      this.materialDecFormControl.disable();
      this.serviceDecFormControl.disable();
      this.otherDecFormControl.disable();
    }
  }

  ngOnInit(): void {
    this.projectTotalsForm = this.fb.group({
      janMaterial: this.materialJanFormControl,
      febMaterial: this.materialFebFormControl,
      marMaterial: this.materialMarFormControl,
      aprMaterial: this.materialAprFormControl,
      mayMaterial: this.materialMayFormControl,
      junMaterial: this.materialJunFormControl,
      julMaterial: this.materialJulFormControl,
      augMaterial: this.materialAugFormControl,
      sepMaterial: this.materialSepFormControl,
      octMaterial: this.materialOctFormControl,
      novMaterial: this.materialNovFormControl,
      decMaterial: this.materialDecFormControl,
      janService: this.serviceJanFormControl,
      febService: this.serviceFebFormControl,
      marService: this.serviceMarFormControl,
      aprService: this.serviceAprFormControl,
      mayService: this.serviceMayFormControl,
      junService: this.serviceJunFormControl,
      julService: this.serviceJulFormControl,
      augService: this.serviceAugFormControl,
      sepService: this.serviceSepFormControl,
      octService: this.serviceOctFormControl,
      novService: this.serviceNovFormControl,
      decService: this.serviceDecFormControl,
      janOther: this.otherJanFormControl,
      febOther: this.otherFebFormControl,
      marOther: this.otherMarFormControl,
      aprOther: this.otherAprFormControl,
      mayOther: this.otherMayFormControl,
      junOther: this.otherJunFormControl,
      julOther: this.otherJulFormControl,
      augOther: this.otherAugFormControl,
      sepOther: this.otherSepFormControl,
      octOther: this.otherOctFormControl,
      novOther: this.otherNovFormControl,
      decOther: this.otherDecFormControl,
      janTotal: this.totalJanFormControl,
      febTotal: this.totalFebFormControl,
      marTotal: this.totalMarFormControl,
      aprTotal: this.totalAprFormControl,
      mayTotal: this.totalMayFormControl,
      junTotal: this.totalJunFormControl,
      julTotal: this.totalJulFormControl,
      augTotal: this.totalAugFormControl,
      sepTotal: this.totalSepFormControl,
      octTotal: this.totalOctFormControl,
      novTotal: this.totalNovFormControl,
      decTotal: this.totalDecFormControl,
      totalMaterial: this.totalMaterialFormControl,
      totalService: this.totalServiceFormControl,
      totalOther: this.totalOtherFormControl,
      total: this.totalGeneralFormControl,
    });

    // diable totals
    this.totalJanFormControl.disable();
    this.totalFebFormControl.disable();
    this.totalMarFormControl.disable();
    this.totalAprFormControl.disable();
    this.totalMayFormControl.disable();
    this.totalJunFormControl.disable();
    this.totalJulFormControl.disable();
    this.totalAugFormControl.disable();
    this.totalSepFormControl.disable();
    this.totalOctFormControl.disable();
    this.totalNovFormControl.disable();
    this.totalDecFormControl.disable();
    this.totalMaterialFormControl.disable();
    this.totalServiceFormControl.disable();
    this.totalOtherFormControl.disable();
    this.totalGeneralFormControl.disable();

    // Subscribe to form value changes to update break-in button state
    this.totalGeneralFormControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.data.isForForecasts) {
          this.calculateCanEditBreakInOut();
          // calculateTotals now calls updateBreakInOutButtonState internally
        }
        this.cdr.markForCheck();
      });

    const currentDate = new Date();
    // this.disablePastMonths(currentDate.getMonth());

    // Initialize flags early so the template renders correctly on first pass
    this.isForForecasts = !!this.data.isForForecasts;
    this.isFullAccess = !!this.data.isFullAccess;
    this.initiative = this.data.initiative;
    this.indicatedAmount = this.data.indicatedAmount ?? 0;
    if (this.isFullAccess) {
      this.isSaveAllowed.set(true);
    } else {
      this.isSaveAllowed.set(false);
    }

    this.apiClient.getForecastEditStatus()
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          if (response == 'Opened') {
            this.isForecastOpened = true;
          }

          else
            this.isForecastOpened = false;

          // Pre-execution stages are editable regardless of the forecast window state
          const isEditWindowOpen = this.isForecastOpened || this.isPreExecutionPlanningStage();

          if (this.data.hasPendingBreakInOut || this.data.isFullAccess === false)
            this.disablePastMonths(12); // disable all
          else if (this.data.year && this.data.year > currentDate.getFullYear()) {
            // Future year - allow editing all months (enable all by disabling 0 months)
            if (!isEditWindowOpen) {
              this.disablePastMonths(12); // disable all
            }
          }
          else if (this.data.year && this.data.year === currentDate.getFullYear()) {
            // Current year - apply forecast logic
            isEditWindowOpen ? this.disablePastMonths(currentDate.getMonth()) : this.disablePastMonths(12);
          }
          else if (this.data.year && this.data.year < currentDate.getFullYear()) {
            // Past year - disable all months
            this.disablePastMonths(12);
          }
          if (this.data.isCarriedOver) {
            this.disablePastMonths(12); // disable all
          }
          this.cdr.markForCheck();
        }
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  openPendingBreakInOut() {
    const initiativeId = this.data.initiative?.pk;
    if (!initiativeId || this.loading) return;

    this.loading = true;
    this.loader.setSuppressed(true);
    this.apiClient.onePBreakInOutAll(initiativeId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loader.setSuppressed(false);
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((items) => {
        const pending = items.find(x => x.status === BreakInOutStatus.Pending || x.decision === BreakInOutDecision.OnHold);
        if (!pending) return;

        const urlTree = this.router.parseUrl(this.router.url);
        const source = urlTree.queryParams['source'] ?? urlTree.root.children['primary']?.segments[0]?.path;

        this.dialogRef.close();
        this.router.navigate(['/initiatives/edit', initiativeId, 'break-in-out-details'], {
          state: { rowData: pending },
          queryParams: { initiativeId, breakinId: pending.pk, source, breakInSource: 'initiatives' },
        });
      });
  }

  onBreakInOut() {
    // if (this.projectTotalsForm.valid) {
    //   const currentDate = new Date();
    //   const currentMonth = currentDate.getMonth() + 1;
    //   const formValues = this.projectTotalsForm.getRawValue();

    //   const futureMonths = Object.keys(formValues).reduce((acc, key) => {
    //     const monthMapping: { [key: string]: number } = {
    //       jan: 1,
    //       feb: 2,
    //       mar: 3,
    //       apr: 4,
    //       may: 5,
    //       jun: 6,
    //       jul: 7,
    //       aug: 8,
    //       sep: 9,
    //       oct: 10,
    //       nov: 11,
    //       dec: 12,
    //     };

    //     const monthKey = key.toLowerCase().substring(0, 3);
    //     if (monthMapping[monthKey] && monthMapping[monthKey] >= currentMonth) {
    //       acc[key] = formValues[key];
    //     }
    //     return acc;
    //   }, {} as { [key: string]: any });

    //   Object.assign(this.data.onePProjectTotals, futureMonths);

    //   this.dialogRef.close({
    //     costTotals: this.data.onePProjectTotals,
    //     needToCreateBreakInOut: true,
    //     total: this.totalGeneral
    //   } as CostTotalsEditModel);
    // }
    // Run the regular save flow so unsaved edits are persisted before the break-in is created
    this.onSave(true);
  }

  editActuals() {
    const currentDate = new Date();
    this.isEditingActuals = true;
    if (this.isPowerUserStaged()) {
      this.enablePastMonths(currentDate.getMonth())
    }



  }

  onSave(needToCreateBreakInOut = false): void {
    if (this.projectTotalsForm.valid) {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const formValues = this.projectTotalsForm.getRawValue();

      const monthMapping: { [key: string]: number } = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };

      const futureMonths: { [key: string]: any } = {};
      const pastMonths: { [key: string]: any } = {};
      const selectedYear = this.data.year || currentYear;
      let futureForecastChanged = false;

      Object.keys(formValues).forEach((key) => {
        if (key.toLowerCase().includes('total')) {
          return;
        }
        const monthKey = key.toLowerCase().substring(0, 3);
        if (!monthMapping[monthKey]) {
          return;
        }

        // Explicitly determine month category based on year relationship:
        // - Future year  → all months are future
        // - Past year    → all months are past
        // - Current year → split by month (current month treated as future)
        let isFutureMonth: boolean;
        if (selectedYear > currentYear) {
          isFutureMonth = true;
        } else if (selectedYear < currentYear) {
          isFutureMonth = false;
        } else {
          isFutureMonth = monthMapping[monthKey] >= currentMonth;
        }

        const initialValue = this.data.onePProjectTotals[key];
        const currentValue = formValues[key];

        if (isFutureMonth) {
          futureMonths[key] = currentValue != null ? currentValue : 0;
          if ((initialValue ?? 0) !== (currentValue ?? 0)) {
            futureForecastChanged = true;
          }
        } else {
          // For a past year include all months; for the current year only include changed months
          if (selectedYear < currentYear || initialValue !== currentValue) {
            pastMonths[key] = currentValue;
          }
        }
      });

      Object.assign(this.data.onePProjectTotals, futureMonths);
      Object.assign(this.data.onePastProjectTotals, pastMonths);

      const newPastMontals = Object.keys(this.data.onePastProjectTotals)
        .filter(key => {
          const monthKey = key.toLowerCase().substring(0, 3);
          if (!monthMapping[monthKey]) {
            return false;
          }
          if (selectedYear < currentYear) {
            // Past year: all months are valid past months
            return true;
          } else if (selectedYear === currentYear) {
            // Current year: only months strictly before the current month
            return monthMapping[monthKey] < currentMonth;
          }
          // Future year: no past months
          return false;
        }).reduce((acc, key) => {
          acc[key] = this.data.onePastProjectTotals[key];
          return acc;
        }, {} as { [key: string]: any });

      if (this.isEditingActuals) {
        this.apiClient.onePGeneralInitiativePOST2(this.data.initiative.pk, newPastMontals as OnePProjectCostTotalsDTO).subscribe();
        this.dialogRef.close({
          costTotals: this.data.onePastProjectTotals,
          needToCreateBreakInOut: false,
          isActualChanged: true,
          total: this.totalGeneral
        } as CostTotalsEditModel);
      }
      else {
        const isForecastEditableStage =
          this.stage === InitiativeStage.PreExecution ||
          this.stage === InitiativeStage.Execution;

        if (this.isForecastOpened && isForecastEditableStage && futureForecastChanged && !!this.data.initiative?.pk) {
          this.apiClient.updateForecastUpdatedField(new UpdateForecastUpdatedFieldCommand({
            initiativeId: this.data.initiative.pk,
            isForecastUpdated: true,
          })).subscribe();
        }

        this.dialogRef.close({
          costTotals: this.data.onePProjectTotals,
          needToCreateBreakInOut,
          onlyEditedFutureMonths: futureMonths,
          isActualChanged: false,
          total: this.totalGeneral
        } as CostTotalsEditModel);
      }

    }
  }

  calculateTotals(): void {
    let materialJan = this.materialJanFormControl.value as number;
    let materialFeb = this.materialFebFormControl.value as number;
    let materialMar = this.materialMarFormControl.value as number;
    let materialApr = this.materialAprFormControl.value as number;
    let materialMay = this.materialMayFormControl.value as number;
    let materialJun = this.materialJunFormControl.value as number;
    let materialJul = this.materialJulFormControl.value as number;
    let materialAug = this.materialAugFormControl.value as number;
    let materialSep = this.materialSepFormControl.value as number;
    let materialOct = this.materialOctFormControl.value as number;
    let materialNov = this.materialNovFormControl.value as number;
    let materialDec = this.materialDecFormControl.value as number;
    let serviceJan = this.serviceJanFormControl.value as number;
    let serviceFeb = this.serviceFebFormControl.value as number;
    let serviceMar = this.serviceMarFormControl.value as number;
    let serviceApr = this.serviceAprFormControl.value as number;
    let serviceMay = this.serviceMayFormControl.value as number;
    let serviceJun = this.serviceJunFormControl.value as number;
    let serviceJul = this.serviceJulFormControl.value as number;
    let serviceAug = this.serviceAugFormControl.value as number;
    let serviceSep = this.serviceSepFormControl.value as number;
    let serviceOct = this.serviceOctFormControl.value as number;
    let serviceNov = this.serviceNovFormControl.value as number;
    let serviceDec = this.serviceDecFormControl.value as number;
    let otherJan = this.otherJanFormControl.value as number;
    let otherFeb = this.otherFebFormControl.value as number;
    let otherMar = this.otherMarFormControl.value as number;
    let otherApr = this.otherAprFormControl.value as number;
    let otherMay = this.otherMayFormControl.value as number;
    let otherJun = this.otherJunFormControl.value as number;
    let otherJul = this.otherJulFormControl.value as number;
    let otherAug = this.otherAugFormControl.value as number;
    let otherSep = this.otherSepFormControl.value as number;
    let otherOct = this.otherOctFormControl.value as number;
    let otherNov = this.otherNovFormControl.value as number;
    let otherDec = this.otherDecFormControl.value as number;
    let totalJan = Math.round((materialJan + serviceJan + otherJan) * 100) / 100;
    this.totalJanFormControl.setValue(totalJan);
    let totalFeb = Math.round((materialFeb + serviceFeb + otherFeb) * 100) / 100;
    this.totalFebFormControl.setValue(totalFeb);
    let totalMar = Math.round((materialMar + serviceMar + otherMar) * 100) / 100;
    this.totalMarFormControl.setValue(totalMar);
    let totalApr = Math.round((materialApr + serviceApr + otherApr) * 100) / 100;
    this.totalAprFormControl.setValue(totalApr);
    let totalMay = Math.round((materialMay + serviceMay + otherMay) * 100) / 100;
    this.totalMayFormControl.setValue(totalMay);
    let totalJun = Math.round((materialJun + serviceJun + otherJun) * 100) / 100;
    this.totalJunFormControl.setValue(totalJun);
    let totalJul = Math.round((materialJul + serviceJul + otherJul) * 100) / 100;
    this.totalJulFormControl.setValue(totalJul);
    let totalAug = Math.round((materialAug + serviceAug + otherAug) * 100) / 100;
    this.totalAugFormControl.setValue(totalAug);
    let totalSep = Math.round((materialSep + serviceSep + otherSep) * 100) / 100;
    this.totalSepFormControl.setValue(totalSep);
    let totalOct = Math.round((materialOct + serviceOct + otherOct) * 100) / 100;
    this.totalOctFormControl.setValue(totalOct);
    let totalNov = Math.round((materialNov + serviceNov + otherNov) * 100) / 100;
    this.totalNovFormControl.setValue(totalNov);
    let totalDec = Math.round((materialDec + serviceDec + otherDec) * 100) / 100;
    this.totalDecFormControl.setValue(totalDec);
    let totalMaterial =
      materialJan +
      materialFeb +
      materialMar +
      materialApr +
      materialMay +
      materialJun +
      materialJul +
      materialAug +
      materialSep +
      materialOct +
      materialNov +
      materialDec;
    let totalService =
      serviceJan +
      serviceFeb +
      serviceMar +
      serviceApr +
      serviceMay +
      serviceJun +
      serviceJul +
      serviceAug +
      serviceSep +
      serviceOct +
      serviceNov +
      serviceDec;
    let totalOther =
      otherJan +
      otherFeb +
      otherMar +
      otherApr +
      otherMay +
      otherJun +
      otherJul +
      otherAug +
      otherSep +
      otherOct +
      otherNov +
      otherDec;
    this.totalMaterialFormControl.setValue(Math.round(totalMaterial * 1000) / 1000);
    this.totalServiceFormControl.setValue(Math.round(totalService * 1000) / 1000);
    this.totalOtherFormControl.setValue(Math.round(totalOther * 1000) / 1000);
    this.totalGeneral = Math.round((totalMaterial + totalService + totalOther) * 1000) / 1000;
    this.totalGeneralFormControl.setValue(this.totalGeneral);
    if (this.data.isForForecasts) {
      this.noChanges.set(!this.compareInitiativeCostTotals());
    }
  }

  private enablePastMonths(monthIndex: number) {
    if (this.authService.isInRole(Roles.PowerUser) && (this.stage === InitiativeStage.Execution || this.stage === InitiativeStage.PreExecution)) {
      if (monthIndex >= 1) {
        this.materialJanFormControl.enable();
        this.serviceJanFormControl.enable();
        this.otherJanFormControl.enable();
      }
      else {
        this.materialJanFormControl.disable();
        this.serviceJanFormControl.disable();
        this.otherJanFormControl.disable();
      }
      if (monthIndex >= 2) {
        this.materialFebFormControl.enable();
        this.serviceFebFormControl.enable();
        this.otherFebFormControl.enable();
      } else {
        this.materialFebFormControl.disable();
        this.serviceFebFormControl.disable();
        this.otherFebFormControl.disable();
      }
      if (monthIndex >= 3) {
        this.materialMarFormControl.enable();
        this.serviceMarFormControl.enable();
        this.otherMarFormControl.enable();
      } else {
        this.materialMarFormControl.disable();
        this.serviceMarFormControl.disable();
        this.otherMarFormControl.disable();
      }
      if (monthIndex >= 4) {
        this.materialAprFormControl.enable();
        this.serviceAprFormControl.enable();
        this.otherAprFormControl.enable();
      } else {
        this.materialAprFormControl.disable();
        this.serviceAprFormControl.disable();
        this.otherAprFormControl.disable();
      }
      if (monthIndex >= 5) {
        this.materialMayFormControl.enable();
        this.serviceMayFormControl.enable();
        this.otherMayFormControl.enable();
      } else {
        this.materialMayFormControl.disable();
        this.serviceMayFormControl.disable();
        this.otherMayFormControl.disable();
      }
      if (monthIndex >= 6) {
        this.materialJunFormControl.enable();
        this.serviceJunFormControl.enable();
        this.otherJunFormControl.enable();
      } else {
        this.materialJunFormControl.disable();
        this.serviceJunFormControl.disable();
        this.otherJunFormControl.disable();
      }
      if (monthIndex >= 7) {
        this.materialJulFormControl.enable();
        this.serviceJulFormControl.enable();
        this.otherJulFormControl.enable();
      } else {
        this.materialJulFormControl.disable();
        this.serviceJulFormControl.disable();
        this.otherJulFormControl.disable();
      }
      if (monthIndex >= 8) {
        this.materialAugFormControl.enable();
        this.serviceAugFormControl.enable();
        this.otherAugFormControl.enable();
      } else {
        this.materialAugFormControl.disable();
        this.serviceAugFormControl.disable();
        this.otherAugFormControl.disable();
      }
      if (monthIndex >= 9) {
        this.materialSepFormControl.enable();
        this.serviceSepFormControl.enable();
        this.otherSepFormControl.enable();
      } else {
        this.materialSepFormControl.disable();
        this.serviceSepFormControl.disable();
        this.otherSepFormControl.disable();
      }
      if (monthIndex >= 10) {
        this.materialOctFormControl.enable();
        this.serviceOctFormControl.enable();
        this.otherOctFormControl.enable();
      } else {
        this.materialOctFormControl.disable();
        this.serviceOctFormControl.disable();
        this.otherOctFormControl.disable();
      }
      if (monthIndex >= 11) {
        this.materialNovFormControl.enable();
        this.serviceNovFormControl.enable();
        this.otherNovFormControl.enable();
      } else {
        this.materialNovFormControl.disable();
        this.serviceNovFormControl.disable();
        this.otherNovFormControl.disable();
      }
      if (monthIndex >= 12) {
        this.materialDecFormControl.enable();
        this.serviceDecFormControl.enable();
        this.otherDecFormControl.enable();
      } else {
        this.materialDecFormControl.disable();
        this.serviceDecFormControl.disable();
        this.otherDecFormControl.disable();
      }
    }
  }
  shouldHighlightCell(month: string, category: string): boolean {
    const formControlKey = `${month}${category.charAt(0).toUpperCase() + category.slice(1)}`;
    const formControlValue = this.projectTotalsForm.get(formControlKey)?.value;
    const initialValue = this.initialFormValues[formControlKey];
    const currentDate = new Date();
    if (currentDate.getFullYear() !== this.data.year) {
      return false; // Don't highlight if the year is not the current year
    }

    if (formControlValue !== initialValue && formControlValue !== 0 && formControlValue !== null) {
      return true;
    }

    const key = `${month}${category.charAt(0).toUpperCase() + category.slice(1)}`;
    const isFlagged = (this.initiativeActualCostTotalsEditedFlags as any)[key] === true;

    if (!isFlagged) return false;

    const monthMapping: { [key: string]: number } = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12
    };

    const currentMonth = currentDate.getMonth() + 1;
    const cellMonth = monthMapping[month.toLowerCase()];

    if (cellMonth >= currentMonth) return false;

    if (formControlValue === 0 || formControlValue === null) return false;

    return true;
  }
  calculateCanEditBreakInOut(): void {
    if (!this.data.isForForecasts) return;

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    if (this.data.year && this.data.year < currentYear) {
      this.canAddBreakInOut = false;
      return;
    }
    if (this.isReadOnlyUser) return;
    const isActiveStage = this.initiative.stage === InitiativeStage.PreExecution || this.initiative.stage === InitiativeStage.Execution;
    if (!isActiveStage || !this.initiative.pk) return;

    const newFyOutlook = this.costTotalsService.calculateTotals(this.projectTotalsForm.getRawValue());
    const newFyOutlookGT = Math.floor(newFyOutlook + (this.carriedOverFyOutlook ?? 0));
    const lastMonthGT = Math.round((this.lastMonthFyOutlook ?? 0) + (this.lastMonthCarriedOverFyOutlook ?? 0));
    const projectMonth = new Date(this.initiative.createdOn).getMonth() + 1;
    const projectYear = new Date(this.initiative.createdOn).getFullYear();
    const indicatedAmount = this.indicatedAmount ?? 0;

    if (Math.abs(newFyOutlookGT - lastMonthGT) > 5) {
      if (projectYear >= currentYear && projectMonth == currentMonth && this.initiative?.fyOutlook == newFyOutlook) {
        this.canAddBreakInOut = false;
      }
      else if (Math.abs(newFyOutlookGT - lastMonthGT) < indicatedAmount) {
        this.canAddBreakInOut = false;
      }
      else {
        this.canAddBreakInOut = true;
      }
    } else {
      this.canAddBreakInOut = false;

    }

  }

}


